import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { api, type Structure } from "../lib/api";
import { StructureRenderer } from "../lib/renderer";
import { loadCore, type MeshOverlay, type SchematicInfo, type StudioCore } from "../lib/wasm";
import type { GeometryOp } from "./Inspector";

export interface ViewerHandle {
  show(s: Structure): Promise<void>;
  // The rule's fields changed (the y offset in particular): re-mesh from the
  // structure already loaded, without fetching its bytes again.
  update(s: Structure): void;
  clear(): void;
  // Runs a geometry op in the core and returns the NEW .schem bytes for the
  // caller to upload; null when there was nothing to do.
  applyGeometry(op: GeometryOp): Promise<Uint8Array | null>;
}

const DEFAULT_OVERLAY: MeshOverlay = {
  yOffset: 0,
  ground: true,
  footprint: true,
  grid: true,
  markSunken: true,
  showFoundation: true,
  sliceTop: -1,
};

// The 3D view. It shows a build the way the export will seat it: on the ground
// at the height the y offset puts it, inside the footprint the earthworks pass
// levels, with the ground it cuts away and the foundation piers it grows.

export const Viewer = forwardRef<ViewerHandle, { onError: (m: string) => void }>(
  function Viewer(props, ref) {
    const canvas = useRef<HTMLCanvasElement>(null);
    const renderer = useRef<StructureRenderer | null>(null);
    const core = useRef<StudioCore | null>(null);
    const handle = useRef<SchematicInfo | null>(null);
    const structure = useRef<Structure | null>(null);
    const [overlay, setOverlayState] = useState<MeshOverlay>(DEFAULT_OVERLAY);
    // The imperative handle and the mesh builder read the CURRENT overlay, not
    // the one captured when the callback was created -- a stale copy silently
    // undoes whatever was just changed.
    const overlayRef = useRef<MeshOverlay>(DEFAULT_OVERLAY);
    const setOverlay = (o: MeshOverlay) => {
      overlayRef.current = o;
      setOverlayState(o);
    };
    const [info, setInfo] = useState<SchematicInfo | null>(null);
    const [status, setStatus] = useState("Loading the renderer…");
    const [assets, setAssets] = useState<"none" | "loading" | "ready">("none");

    // --- one-time setup ------------------------------------------------------
    useEffect(() => {
      if (!canvas.current) return;
      try {
        renderer.current = new StructureRenderer(canvas.current);
      } catch (e) {
        setStatus(String((e as Error).message));
        return;
      }
      const onResize = () => renderer.current?.draw();
      window.addEventListener("resize", onResize);

      loadCore()
        .then(async (c) => {
          core.current = c;
          setStatus("");
          // Pull the client jar once; the browser caches it (it is ~40 MB) and
          // the core bakes the atlas from it.
          setAssets("loading");
          try {
            const res = await fetch("/api/assets/client.jar", {
              credentials: "same-origin",
            });
            if (res.ok) {
              const jar = new Uint8Array(await res.arrayBuffer());
              if (c.setAssets(jar)) {
                renderer.current?.setAtlas(c.atlas());
                setAssets("ready");
              } else {
                setAssets("none");
                props.onError(
                  "The configured jar carries no block assets — flat colours are used.",
                );
              }
            } else {
              setAssets("none");
            }
          } catch {
            setAssets("none");
          }
          if (structure.current) void showInternal(structure.current);
        })
        .catch((e) => setStatus(String((e as Error).message)));

      return () => {
        window.removeEventListener("resize", onResize);
        renderer.current?.dispose();
        renderer.current = null;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- drawing -------------------------------------------------------------
    const rebuild = (ov: MeshOverlay) => {
      const c = core.current;
      const h = handle.current;
      if (!c || !h || !renderer.current) return;
      renderer.current.setMesh(c.buildMesh(h.handle, ov));
    };

    const showInternal = async (s: Structure) => {
      structure.current = s;
      const c = core.current;
      if (!c || !renderer.current) return;
      if (handle.current) c.freeSchematic(handle.current.handle);
      handle.current = null;
      const bytes = await api.schem(s.id);
      const loaded = c.loadSchematic(bytes);
      handle.current = loaded;
      setInfo(loaded);
      const ov = { ...overlayRef.current, yOffset: s.yOffset, bury: s.bury, sliceTop: -1 };
      setOverlay(ov);
      renderer.current.frame(loaded.sx, loaded.sy, loaded.sz);
      rebuild(ov);
    };

    useImperativeHandle(ref, () => ({
      show: async (s) => {
        try {
          await showInternal(s);
        } catch (e) {
          props.onError(String((e as Error).message));
        }
      },
      update: (s) => {
        structure.current = s;
        const ov = { ...overlayRef.current, yOffset: s.yOffset, bury: s.bury };
        setOverlay(ov);
        rebuild(ov);
      },
      clear: () => {
        structure.current = null;
        if (core.current && handle.current)
          core.current.freeSchematic(handle.current.handle);
        handle.current = null;
        setInfo(null);
        renderer.current?.clear();
      },
      applyGeometry: async (op) => {
        const c = core.current;
        const h = handle.current;
        if (!c || !h) {
          props.onError("The renderer core is not loaded — cannot edit geometry.");
          return null;
        }
        switch (op) {
          case "trim": {
            const r = c.trim(h.handle);
            // A bottom trim moves the build's floor, so the offset follows or
            // the build would jump out of the ground.
            if (r.removed[1] && structure.current)
              await api.patch(structure.current.id, {
                yOffset: structure.current.yOffset + r.removed[1],
              });
            return r.bytes;
          }
        }
      },
    }));

    const set = (patch: Partial<MeshOverlay>) => {
      const ov = { ...overlayRef.current, ...patch };
      setOverlay(ov);
      rebuild(ov);
    };

    return (
      <div className="viewer">
        <canvas ref={canvas} />
        {status && <div className="overlay-message">{status}</div>}

        <div className="viewbar">
          <label>
            <input
              type="checkbox"
              checked={overlay.ground}
              onChange={(e) => set({ ground: e.target.checked })}
            />
            Boden
          </label>
          <label>
            <input
              type="checkbox"
              checked={overlay.footprint}
              onChange={(e) => set({ footprint: e.target.checked })}
            />
            Grundfläche
          </label>
          
          <label title="Fundament-Pfeiler, die der Export unter überhängende Spalten bis zum Boden zieht">
            <input
              type="checkbox"
              checked={overlay.showFoundation}
              onChange={(e) => set({ showFoundation: e.target.checked })}
            />
            Fundamente
          </label>
          {info && (
            <input
              className="slice"
              type="range"
              min={0}
              max={info.sy}
              value={overlay.sliceTop < 0 ? info.sy : overlay.sliceTop}
              onChange={(e) => {
                const v = Number(e.target.value);
                set({ sliceTop: v >= info.sy ? -1 : v });
              }}
              title="slice the upper layers away"
            />
          )}
        </div>

        {info && (
          <div className="stats">
            {info.sx} × {info.sy} × {info.sz} · {info.placed} Blöcke ·{" "}
            {info.air} Luft (gräbt aus) · {info.voidAir} unberührt
            {info.groundMarks > 0 && (
              <span
                className="marks"
                title="Bedrock im Bauwerk ist kein Block, sondern die Marke „hier muss fester Boden sein“. Der Export lässt vorhandenen Boden stehen und füllt nur Luft und Wasser — Bedrock wird nie gesetzt."
              >
                {" · "}
                {info.groundMarks} Boden-Marken
              </span>
            )}
            {" · "}
            {assets === "ready"
              ? "texturiert"
              : assets === "loading"
                ? "lädt Texturen…"
                : "einfarbig"}
          </div>
        )}
      </div>
    );
  },
);
